import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, LinearProgress } from '@mui/material';
import { Close as CloseIcon, Rocket as RocketIcon } from '@mui/icons-material';
import { subscriptionAPI } from '../services/api';

const COLORS = {
  green: { bg: '#052e16', border: '#22c55e', text: '#22c55e', bar: '#22c55e' },
  amber: { bg: '#422006', border: '#f59e0b', text: '#f59e0b', bar: '#f59e0b' },
  red:   { bg: '#450a0a', border: '#ef4444', text: '#ef4444', bar: '#ef4444' },
  gray:  { bg: '#1e293b', border: '#64748b', text: '#cbd5e1', bar: '#64748b' },
};

function getTheme(daysLeft, status) {
  if (status === 'active') return null; // no banner
  if (status === 'read-only') return COLORS.gray;
  if (daysLeft <= 2) return COLORS.red;
  if (daysLeft <= 5) return COLORS.amber;
  return COLORS.green;
}

export default function TrialBanner() {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    subscriptionAPI.getStatus()
      .then(res => { if (mounted) setSub(res.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Don't show if loading, no data, active subscription, or dismissed
  if (loading || !sub || sub.status === 'active' || dismissed) return null;

  const theme = getTheme(sub.daysLeft, sub.status);
  if (!theme) return null;

  const isReadOnly = sub.status === 'read-only';
  const progress = isReadOnly ? 100 : Math.max(0, ((14 - sub.daysLeft) / 14) * 100);

  return (
    <Box sx={{
      bgcolor: theme.bg,
      borderBottom: `1px solid ${theme.border}`,
      px: 2, py: 1,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      position: 'relative',
      zIndex: 1300,
    }}>
      {/* Progress bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          bgcolor: 'rgba(255,255,255,0.05)',
          '& .MuiLinearProgress-bar': { bgcolor: theme.bar },
        }}
      />

      <RocketIcon sx={{ color: theme.text, fontSize: 20 }} />

      <Typography sx={{ flex: 1, color: '#f1f5f9', fontSize: '0.82rem', fontWeight: 500 }}>
        {isReadOnly ? (
          <>
            Your free trial has ended.{' '}
            <Typography component="span" sx={{ color: theme.text, fontWeight: 700 }}>
              Account is read-only
            </Typography>
            {' '}&mdash; upgrade to restore full access. Data preserved for 30 days.
          </>
        ) : (
          <>
            Free trial:{' '}
            <Typography component="span" sx={{ color: theme.text, fontWeight: 700 }}>
              {sub.daysLeft} day{sub.daysLeft !== 1 ? 's' : ''} left
            </Typography>
            {sub.daysLeft <= 5 && ' \u2014 upgrade now to avoid interruption.'}
          </>
        )}
      </Typography>

      <Button
        size="small"
        variant="contained"
        onClick={() => { window.location.href = '/settings?tab=billing'; }}
        sx={{
          bgcolor: theme.border,
          color: '#fff',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.78rem',
          borderRadius: '8px',
          px: 2.5,
          py: 0.5,
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: theme.text, filter: 'brightness(1.1)' },
        }}
      >
        {isReadOnly ? 'Upgrade Now' : 'Upgrade'}
      </Button>

      {!isReadOnly && (
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          sx={{ color: '#64748b', '&:hover': { color: '#94a3b8' } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );
}
